'use client'

import {useCallback, useEffect, useState} from 'react'
import {apiDelete, apiGet, apiPatch, apiPost} from '@/lib/api'
import type {Account, ContactLabel} from '@/types'

type UseLabelsResult = {
    labels: ContactLabel[]
    loading: boolean
    refreshing: boolean
    /** Re-read the local cache from the DB. */
    reload: () => Promise<void>
    /** Ask the server to pull the canonical list from Go and overwrite the cache. */
    refresh: () => Promise<void>
    /** Create a new label on WeChat (proxied through Go). Returns updated list. */
    create: (name: string) => Promise<ContactLabel[]>
    /** Rename a label. Returns updated list. */
    rename: (id: number, name: string) => Promise<ContactLabel[]>
    /** Delete a label. Returns updated list. */
    remove: (id: number) => Promise<ContactLabel[]>
}

/**
 * Hook that exposes the logged-in account's WeChat contact labels.
 *
 * - `reload` hits `GET /api/labels` (local cache, cheap)
 * - `refresh` hits `POST /api/labels/refresh` which proxies the Go
 *   `/Label/GetList` endpoint and overwrites the cache. Used by the 刷新
 *   button on the labels page.
 * - `create` / `rename` / `remove` proxy /Label/Add, /Label/UpdateName,
 *   /Label/Delete on the server. Each endpoint refreshes the cache
 *   server-side, so the response already contains the authoritative list.
 *
 * Automatically loads from cache when the account changes.
 */
export function useLabels(account: Account | null): UseLabelsResult {
    const [labels, setLabels] = useState<ContactLabel[]>([])
    const [loading, setLoading] = useState(false)
    const [refreshing, setRefreshing] = useState(false)

    const reload = useCallback(async () => {
        if (!account) {
            setLabels([])
            return
        }
        setLoading(true)
        try {
            const qs = 'wxid=' + encodeURIComponent(account.wxid)
            const response = await apiGet<{ success: boolean; labels: ContactLabel[] }>(
                `/labels?${qs}`,
            )
            setLabels(response.labels || [])
        } finally {
            setLoading(false)
        }
    }, [account])

    const refresh = useCallback(async () => {
        if (!account) return
        setRefreshing(true)
        try {
            const response = await apiPost<{ success: boolean; labels: ContactLabel[] }>(
                '/labels/refresh',
                {wxid: account.wxid},
            )
            setLabels(response.labels || [])
        } finally {
            setRefreshing(false)
        }
    }, [account])

    const create = useCallback(
        async (name: string) => {
            if (!account) return []
            setRefreshing(true)
            try {
                const response = await apiPost<{ success: boolean; labels: ContactLabel[] }>(
                    '/labels',
                    {wxid: account.wxid, name},
                )
                const next = response.labels || []
                setLabels(next)
                return next
            } finally {
                setRefreshing(false)
            }
        },
        [account],
    )

    const rename = useCallback(
        async (id: number, name: string) => {
            if (!account) return []
            setRefreshing(true)
            try {
                const response = await apiPatch<{ success: boolean; labels: ContactLabel[] }>(
                    `/labels/${id}?wxid=${encodeURIComponent(account.wxid)}`,
                    {name},
                )
                const next = response.labels || []
                setLabels(next)
                return next
            } finally {
                setRefreshing(false)
            }
        },
        [account],
    )

    const remove = useCallback(
        async (id: number) => {
            if (!account) return []
            setRefreshing(true)
            try {
                const response = await apiDelete<{ success: boolean; labels: ContactLabel[] }>(
                    `/labels/${id}?wxid=${encodeURIComponent(account.wxid)}`,
                )
                const next = response.labels || []
                setLabels(next)
                return next
            } finally {
                setRefreshing(false)
            }
        },
        [account],
    )

    useEffect(() => {
        void reload()
    }, [reload])

    return {labels, loading, refreshing, reload, refresh, create, rename, remove}
}
